import * as Sentry from '@sentry/react';
import {
  DescriptionPage_CourseType as DescriptionPageCourseType,
  DescriptionPage_ProductVariant as DescriptionPageProductVariant,
  DescriptionPage_MarketingSource as MarketingSource,
} from '__generated__/graphql-types';

import config from 'js/app/config';

import ROUTE_CONFIG from 'bundles/unified-description-page-common/constants/routeConfig';
import getXDPPageLink from 'bundles/unified-description-page-common/utils/getXDPPageLink';

import _t from 'i18n!nls/xdp';

export const getSpecializationProductUrl = ({
  productSlug,
  productVariant,
  marketingSource,
  programSlug,
  orgSlug,
  localizedLanguageRegionCode,
}: {
  productSlug: string;
  productVariant: DescriptionPageProductVariant;
  marketingSource?: MarketingSource;
  programSlug?: string;
  orgSlug?: string;
  localizedLanguageRegionCode?: string;
}) => {
  let basePath = '';

  // If org slug is included we direct to org-scoped enterprise pages.
  // Otherwise, if program slug is included we direct to program-scoped enterprise pages.
  if (orgSlug && !marketingSource) {
    basePath = `/organizations/${orgSlug}`;
  } else if (programSlug && !marketingSource) {
    basePath = `/programs/${programSlug}`;
  }

  if (marketingSource === MarketingSource.Google) {
    if (productVariant === DescriptionPageProductVariant.ProfessionalCertificate) {
      basePath += `/${ROUTE_CONFIG.GOOGLE_CERTIFICATES.pathname}/${productSlug}`;
    } else {
      basePath += `/${ROUTE_CONFIG.GOOGLE_SPECIALIZATION.pathname}/${productSlug}`;
    }
  } else {
    if (productVariant === DescriptionPageProductVariant.ProfessionalCertificate) {
      basePath += `/${ROUTE_CONFIG.PROFESSIONAL_CERTIFICATES.pathname}/${productSlug}`;
    } else {
      basePath += `/${ROUTE_CONFIG.SPECIALIZATIONS.pathname}/${productSlug}`;
    }
  }

  if (marketingSource && marketingSource === MarketingSource.PaidMedia) {
    basePath = `${basePath}/paidmedia`;
  }

  return getXDPPageLink({ productPageUrlPath: basePath, subfolderLocaleCode: localizedLanguageRegionCode });
};

export const getCourseProductUrl = (
  productSlug: string,
  isProject?: boolean,
  programSlug?: string,
  orgSlug?: string
) => {
  let basePath = '';

  // If org slug is included we direct to org-scoped enterprise pages.
  // Otherwise, if program slug is included we direct to program-scoped enterprise pages.
  if (orgSlug) {
    basePath = `/organizations/${orgSlug}`;
  } else if (programSlug) {
    basePath = `/programs/${programSlug}`;
  }

  if (isProject) {
    return basePath + `/projects/${productSlug}`;
  } else {
    return basePath + `/learn/${productSlug}`;
  }
};

export const getDegreeDescriptionPageUrl = (degreeSlug: string) => {
  return `/degrees/${degreeSlug}`;
};

export const getInstructorPageUrl = (id: string, shortName?: string | null) => {
  if (shortName) {
    return `/instructor/${shortName}`;
  } else {
    return `/instructor/~${id}`;
  }
};

export const getPartnerPageUrl = (shortName: string): string => {
  if (shortName) {
    return `/partners/${shortName}`;
  }
  return '/';
};

/**
 * We house both "guided project" and "project" course types in ProjectPage.
 * We may consider creating GuidedProjectPage if we see enough deviation b/w the two course types in the future.
 * */
export const checkIsGuidedProject = (
  courseType: (typeof DescriptionPageCourseType)[keyof typeof DescriptionPageCourseType]
): boolean => courseType === DescriptionPageCourseType.RhymeProject;

export const enterpriseCompanyLogos = ['Petrobras', 'TATA', 'Danone', 'Capgemini', 'P&G', "L'Oreal"];

export const getEnterpriseCompanyLogosAltText = (logos: string[]): string => {
  if (logos.length === 0) return '';
  // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  if (logos.length === 1) return logos[0];

  return _t('#{logoList} and #{lastLogo}', { logoList: logos.slice(0, -1).join(', '), lastLogo: logos.slice(-1) });
};

/**
 * This function is used to add isExternal=true to the URL to ensure the pageview tracking is correctly attributed to the external platform in analytics
 * @param pathname - relative path to which isExternal=true needs to be appended
 * @returns URL with isExternal=true appended
 */
export const addExternalPlatformTracking = (pathname: string) => {
  try {
    const fullUrl = `${config.url.base}${pathname.slice(1)}`;
    const urlObj = new URL(fullUrl);
    urlObj.searchParams.append('isExternal', 'true');
    return urlObj.toString();
  } catch (e) {
    // If the URL is malformed, return the original pathname to fail silently but track in sentry
    Sentry?.captureException(e, {
      extra: { message: '[Learner Flow Optimization] Error adding isExternal=true to URL' },
    });
    return pathname;
  }
};

/**
 * Constructs a deep link URL to a specific item in a course, optionally appending isExternal=true for external platform tracking.
 * @param params - Object containing courseSlug, deepLinkItemId, and optional isExternal flag.
 * @returns The constructed deep link URL as a string.
 */
export const constructDeepLinkUrl = ({
  courseSlug,
  deepLinkItemId,
  deepLinkModuleIndex,
  isExternal,
}: {
  courseSlug: string;
  deepLinkItemId?: string;
  deepLinkModuleIndex?: number;
  isExternal?: boolean;
}): string => {
  let basePath = `/learn/${courseSlug}/home`;

  if (deepLinkModuleIndex !== undefined) {
    basePath = `/learn/${courseSlug}/home/module/${deepLinkModuleIndex + 1}`;
  }

  if (deepLinkItemId) {
    basePath = `/learn/${courseSlug}/item/${deepLinkItemId}`;
  }

  if (isExternal) {
    return addExternalPlatformTracking(basePath);
  }

  return basePath;
};
